import { useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useAuth } from '../lib/auth.jsx'
import { API_BASE } from '../lib/api.js'
import {loginReturnTo} from '../lib/loginReturnTo.js'
import styles from './Login.module.css'

/* The bundle's own content hash, read off the script tag that loaded it.
 *
 * Deliberately not a version number injected at build time — that tells you
 * what the BUILD thought it was, which is exactly the thing that lies when a
 * device is running something older. The filename hash is the one fact that
 * cannot be stale: whatever hash this reports IS the code executing right
 * now, on that device, in that tab. */
const BUILD_STAMP = (() => {
  try {
    const s = document.querySelector('script[src*="/assets/index-"]')
    const m = s && s.getAttribute('src').match(/index-([^.]+)\.js/)
    return m ? m[1] : 'unknown'
  } catch { return 'unknown' }
})()

export default function Login() {
  const { login, isAuthed } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = loginReturnTo(location.state?.from)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  // Already signed in? Skip the form.
  if (isAuthed) return <Navigate to={from} replace />

  async function onSubmit(e) {
    e.preventDefault()
    if (busy) return
    setError('')
    setBusy(true)
    try {
      await login(username.trim(), password)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err?.message || 'Unable to sign in. Please try again.')
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.wrap}>
      {/* ⚠️ THE REAL MARK, NOT A DRAWN ONE. Both this watermark and the seal
          below rendered a hand-drawn five-pointed gold star — a placeholder, and
          the first thing every rep saw, on a phone, on the login screen.
          `public/brand/sr-monogram.png` is the authoritative Southern
          Recognition monogram (512px, RGBA, already owned in-repo and already
          used by the favicons). Served from our own origin; nothing here
          hotlinks a marketing site or a CDN we do not control. */}
      <div className={styles.watermark} aria-hidden="true">
        <img src="/brand/sr-monogram.png" alt="" width="512" height="512" />
      </div>

      <div className={styles.card}>
        <div className={styles.brand}>
          {/* ⚠️ NO DARK CHIP BEHIND IT. The placeholder star sat in a 52px
              near-black rounded square; the real monogram is charcoal line-work
              on transparency and would have been invisible inside it. The mark
              is used as designed, standing on its own, which is what the brand
              files are drawn for. Source is 512px for a 56px box — past 2× on
              any phone. */}
          <div className={styles.seal}>
            <img
              src="/brand/sr-monogram.png"
              alt="Southern Recognition"
              width="56"
              height="56"
            />
          </div>
          <div className={styles.name}>Southern Recognition</div>
          <div className={styles.tagline}>The Platform</div>
        </div>

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}

          <div>
            <label className="field-label" htmlFor="username">
              Username
            </label>
            <input
              id="username"
              className="input"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="field-label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button
            className={`btn btn-primary ${styles.submit}`}
            type="submit"
            disabled={busy || !username || !password}
          >
            {busy ? <span className="spinner" style={{ borderTopColor: '#fff' }} /> : 'Sign In'}
          </button>
        </form>

        <p className={styles.foot}>
          Authorized Southern Recognition representatives only
        </p>

        {/* BUILD STAMP.
          *
          * Three separate times today a login failure could not be diagnosed
          * because there was no way to tell which build a phone was actually
          * executing — the server was provably healthy and the device was
          * provably reaching it, and the gap between those two facts was
          * invisible. Cache-clearing was guessed at twice.
          *
          * A version string costs nothing and turns "it still doesn't work"
          * into a fact anyone can read aloud. `api` shows where this build
          * thinks the backend lives: `same-origin` is correct, anything else
          * means it is talking cross-domain and is the old bundle. */}
        <p className={styles.foot} style={{ opacity: 0.45, fontSize: '0.7rem', marginTop: 6 }}>
          build {BUILD_STAMP} · api {API_BASE ? API_BASE : 'same-origin'}
        </p>
      </div>
    </div>
  )
}
